import { useSelector } from "react-redux";
import { useState, useEffect } from "react";
import { useSocket } from "../../contexts/SocketContext";
import { getCustomersPage } from "../../services/customer.js";
import "./Customer.scss";
import CustomerItem from "./CustomerItem";
import CustomerOrdersSparkline from "./CustomerOrdersSparkline.jsx";
import {
  Container,
  LoadingOverlay,
  Pagination,
  TextInput,
  Text,
} from "@mantine/core";
import CreateCustomer from "./CreateCustomer.jsx";
import { useSetPageTitle } from "../../hooks/useSetPageTitle";
import { usePermissions } from "../../contexts/PermissionsContext";

const Customer = () => {
  useSetPageTitle("Clientes");
  const { hasPermission } = usePermissions();
  const userData = useSelector((state) => state.user.user);
  const { socket } = useSocket();

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  const getCustomerMethod = async ({ pageOverride, searchOverride } = {}) => {
    try {
      setLoading(true);
      const hasUser = Boolean(userData?._id);
      if (hasUser) {
        const targetPage = Number.isFinite(Number(pageOverride))
          ? Number(pageOverride)
          : currentPage;
        const targetSearch =
          typeof searchOverride === "string" ? searchOverride : searchTerm;
        const page = await getCustomersPage({
          page: targetPage,
          search: targetSearch,
        });
        setCustomers(Array.isArray(page?.items) ? page.items : []);
        if (Number.isFinite(Number(page?.limit))) {
          setPageLimit(Number(page.limit));
        }
        setTotalPages(
          Number.isFinite(Number(page?.totalPages))
            ? Number(page.totalPages)
            : 1
        );
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
      setCustomers([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userData?._id) {
      getCustomerMethod();
    }
  }, [userData]);
  // eslint-disable-next-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (userData?._id) {
      getCustomerMethod();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, userData?._id]);

  useEffect(() => {
    if (!socket) return;
    const onAppUpsert = (payload) => {
      const isCustomers = payload && payload.collection === "customers";
      if (!isCustomers) return;
      getCustomerMethod({ pageOverride: currentPage, searchOverride: searchTerm });
    };
    socket.on("app:upsert", onAppUpsert);
    return () => socket.off("app:upsert", onAppUpsert);
  }, [socket, currentPage, searchTerm]);

  useEffect(() => {
    if (!userData?._id) return;
    const handler = setTimeout(() => {
      getCustomerMethod({ pageOverride: 1, searchOverride: searchTerm });
    }, 1000);
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, userData?._id]);

  return (
    <>
      <LoadingOverlay
        visible={loading}
        overlayProps={{ blur: 2 }}
      />
      <h1 className="hide-on-mobile">Clientes</h1>
      <div
        style={{
          display: "flex",
          gap: 12,
          marginBottom: 16,
          justifyContent: "space-between",
        }}
      >
        <TextInput
          placeholder="Buscar clientes"
          value={searchTerm}
          onChange={(e) => {
            const next = e.target.value;
            setSearchTerm(next);
            setCurrentPage(1);
          }}
          style={{ flex: 1 }}
        />
        {hasPermission({ feature: "clientes", action: "Crear" }) && (
          <CreateCustomer
            loading={loading}
            set_loading={setLoading}
            set_message={() => {}}
            getCustomerMethod={() =>
              getCustomerMethod({
                pageOverride: currentPage,
                searchOverride: searchTerm,
              })
            }
          />
        )}
      </div>
      <div
        className="table-container"
        style={{ textAlign: "left" }}
      >
        {customers.length === 0 ? (
          <Text
            color="dimmed"
            align="center"
          >
            No hay clientes registrados
          </Text>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th >Nombre</th>
                <th >Número telefónico</th>
                <th >Correo electrónico</th>
                <th >Órdenes</th>
                <th ></th>
              </tr>
            </thead>
            <tbody>
              {customers.map((customer) => (
                <CustomerItem
                  key={customer._id}
                  customer={customer}
                  getCustomer={() =>
                    getCustomerMethod({
                      pageOverride: currentPage,
                      searchOverride: searchTerm,
                    })
                  }
                  renderOrdersChart={() => (
                    <div style={{ width: 320 }}>
                      <CustomerOrdersSparkline customerId={customer._id} />
                    </div>
                  )}
                />
              ))}
            </tbody>
          </table>
        )}
        {customers.length > 0 && (
          <div
            style={{
              marginTop: 20,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Pagination
              total={totalPages}
              value={currentPage}
              onChange={setCurrentPage}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default Customer;
